import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { useBetSlip } from "@/context/BetSlipContext";
import { formatOdds, useOddsFormat } from "@/lib/odds";
import { groupPlayerProps } from "@/components/sportsbook/PlayerPropRow";
import { useChrome } from "../ui/MobileShell";

/* MobileEvent (design 3b) — a PUSHED screen (back-chevron + teams title via
   useChrome({mode:'back'})). Event header, underline tabs (Popular / Player props /
   Game props / Team props), stat chips, and one row per player with Over/Under
   side-by-side.

   REAL data: GET /events/:id/markets → { event, groups{moneyline,spread,total},
   props_by_player[] } (polled every 3s, alive-guarded so a slow response for a
   previous id can't flash). Props grouped with the shared groupPlayerProps. Picks
   go through useBetSlip().toggle(leg) with the exact leg shape EventDetail uses
   (including propKey so one side per prop market is enforced). Markets that don't
   exist aren't shown — no fabricated props. */

const signed = (line) => {
  const n = Number(line);
  if (!Number.isFinite(n)) return line ?? "";
  return n > 0 ? `+${n}` : `${n}`;
};

function badgeInitials(name = "") {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  return w[w.length - 1].slice(0, 3).toUpperCase();
}
function playerInitials(name = "") {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

const TABS = [
  { id: "popular", label: "Popular" },
  { id: "props", label: "Player props" },
  { id: "game", label: "Game props" },
];

export default function MobileEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggle, has } = useBetSlip();
  const oddsFmt = useOddsFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("popular");
  const [cat, setCat] = useState(null); // active prop-category (prop_short label)
  const aliveRef = useRef(true);

  const ev = data?.event;
  const title = ev ? `${shortTeam(ev.away_team)} @ ${shortTeam(ev.home_team)}` : "Event";
  useChrome({ mode: "back", title });

  useEffect(() => {
    aliveRef.current = true;
    setData(null); setLoading(true); setNotFound(false);
    const load = async (silent) => {
      try {
        const { data: d } = await api.get(`/events/${id}/markets`);
        if (aliveRef.current) { setData(d); setNotFound(!d?.event); }
      } catch {
        if (!silent && aliveRef.current) setNotFound(true);
      } finally {
        if (aliveRef.current) setLoading(false);
      }
    };
    load();
    const t = setInterval(() => load(true), 3000);
    return () => { aliveRef.current = false; clearInterval(t); };
  }, [id]);

  const groups = data?.groups || { moneyline: [], spread: [], total: [] };
  const players = useMemo(() => data?.props_by_player || [], [data]);
  const eventLabel = ev ? `${ev.away_team} @ ${ev.home_team}` : "";
  const locked = ev && !["open", "live", "paused"].includes(ev.status);

  const choose = (o) => toggle({
    eventId: o.event_id || id, outcomeId: String(o.id), label: o.label, odds: o.odds,
    marketVersion: o.market_version ?? null, line: o.line ?? null, side: o.side ?? null,
    receivedAt: o.received_at ?? null, providerUpdatedAt: o.provider_updated_at ?? null, expiresAt: o.expires_at ?? null,
    eventLabel, market: o.market, league: ev?.league || null, sport: ev?.sport || null, live: ev?.status === "live",
    propKey: o.market === "prop" ? `${o.player || ""}|${o.prop_type || o.prop_short || ""}|${o.line ?? ""}` : null,
  });
  const pickedO = (o) => has(o.event_id || id, String(o.id));

  // Prop categories from the raw props (prop_short labels), for the stat chips.
  const propCats = useMemo(() => {
    const seen = [];
    players.forEach((p) => (p.outcomes || []).forEach((o) => {
      const lbl = prettyProp(o.prop_short);
      if (lbl && !seen.includes(lbl)) seen.push(lbl);
    }));
    return seen;
  }, [players]);

  const activeCat = cat || propCats[0] || null;

  // Players that have at least one outcome in the active category, grouped into rows.
  const propPlayers = useMemo(() => {
    if (!activeCat) return [];
    return players.map((p) => {
      const matching = (p.outcomes || []).filter((o) => prettyProp(o.prop_short) === activeCat);
      if (!matching.length) return null;
      return { player: p.player, rows: groupPlayerProps(matching) };
    }).filter(Boolean);
  }, [players, activeCat]);

  if (loading && !data) return <EventSkeleton />;
  if (notFound || !ev) return (
    <div style={{ textAlign: "center", padding: "60px 16px", color: "#a1a1aa" }}>
      <p style={{ font: "500 15px 'Outfit'" }}>Event not found.</p>
      <button type="button" onClick={() => navigate("/sportsbook")} style={{ marginTop: 16, height: 44, borderRadius: 14, padding: "0 18px", border: 0, cursor: "pointer", background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", color: "#fff", font: "700 14px 'Outfit'" }}>Back to board</button>
    </div>
  );

  const hasLines = (groups.moneyline?.length || groups.spread?.length || groups.total?.length);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12, paddingBottom: 8 }}>
      {/* 1. matchup header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 18, padding: "0 16px" }}>
        <TeamCol name={ev.away_team} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          {ev.status === "live" ? (
            <span style={{ display: "flex", alignItems: "center", gap: 5, font: "800 11px 'Outfit'", letterSpacing: ".1em", color: "#EF4444" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#EF4444", animation: "pnrPulse 1.6s ease-out infinite" }} />
              {ev.gs_period || "LIVE"}
            </span>
          ) : (
            <span style={{ font: "600 11px 'Outfit'", color: "#71717a", whiteSpace: "nowrap" }}>
              {ev.start_time ? new Date(ev.start_time).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }) : "Upcoming"}
            </span>
          )}
          <span style={{ font: "700 13px 'Manrope'", color: "#52525b" }}>vs</span>
        </div>
        <TeamCol name={ev.home_team} />
      </div>

      {/* 2. underline tabs */}
      <div className="m-no-scrollbar" style={{ display: "flex", gap: 20, padding: "0 16px", overflowX: "auto", borderBottom: "1px solid #1F1F23" }}>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              style={{ flex: "none", height: 40, background: "none", border: 0, cursor: "pointer", whiteSpace: "nowrap",
                font: active ? "700 15px 'Outfit'" : "600 15px 'Outfit'", color: active ? "#fff" : "#a1a1aa",
                boxShadow: active ? "inset 0 -2px 0 #00D632" : "none" }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* 3. Popular tab: game lines */}
      {tab === "popular" && (
        hasLines ? <GameLines groups={groups} ev={ev} locked={locked} oddsFmt={oddsFmt} choose={choose} pickedO={pickedO} />
          : <Empty text="No main lines posted for this event yet." />
      )}

      {/* 3b. Game props = same lines block today (no separate game-prop inventory on this event) */}
      {tab === "game" && (
        hasLines ? <GameLines groups={groups} ev={ev} locked={locked} oddsFmt={oddsFmt} choose={choose} pickedO={pickedO} />
          : <Empty text="No game props posted for this event yet." />
      )}

      {/* 4. Player props */}
      {tab === "props" && (
        propCats.length === 0 ? <Empty text="No player props posted for this event yet." /> : (
          <>
            <div className="m-no-scrollbar" style={{ display: "flex", gap: 8, padding: "0 16px", overflowX: "auto" }}>
              {propCats.map((c) => {
                const active = c === activeCat;
                return (
                  <button key={c} type="button" onClick={() => setCat(c)} aria-pressed={active}
                    style={{ flex: "none", height: 34, borderRadius: 999, padding: "0 14px", border: 0, cursor: "pointer",
                      background: active ? "#F4F4F5" : "#18181C", boxShadow: active ? "none" : "inset 0 0 0 1px #26262B",
                      color: active ? "#0A0A0A" : "#d4d4d8", font: active ? "700 13px 'Outfit'" : "600 13px 'Outfit'", whiteSpace: "nowrap" }}>
                    {c}
                  </button>
                );
              })}
            </div>
            <div style={{ margin: "0 16px", borderRadius: 18, overflow: "hidden", background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 44px 74px 74px", columnGap: 6, alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #1A1A1E" }}>
                <span style={{ font: "800 14px 'Manrope'" }}>{activeCat}</span>
                <span />
                <span style={{ textAlign: "center", font: "700 10px 'Outfit'", letterSpacing: ".1em", color: "#71717a" }}>OVER</span>
                <span style={{ textAlign: "center", font: "700 10px 'Outfit'", letterSpacing: ".1em", color: "#71717a" }}>UNDER</span>
              </div>
              {propPlayers.map((pp, pi) => pp.rows.map((r, ri) => (
                <PropRow key={`${pp.player}-${pi}-${ri}`} player={pp.player} row={r} last={pi === propPlayers.length - 1 && ri === pp.rows.length - 1}
                  oddsFmt={oddsFmt} choose={choose} pickedO={pickedO} />
              )))}
            </div>
          </>
        )
      )}
    </div>
  );
}

function GameLines({ groups, ev, locked, oddsFmt, choose, pickedO }) {
  const outs = [...(groups.moneyline || []), ...(groups.spread || []), ...(groups.total || [])];
  const cells = (side) => ({
    ml: outs.find((o) => o.market === "moneyline" && o.side === side),
    spread: outs.find((o) => o.market === "spread" && o.side === side),
    total: outs.find((o) => o.market === "total" && o.side === (side === "away" ? "over" : "under")),
  });
  const away = cells("away");
  const home = cells("home");
  const row = (side, name, c) => (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) repeat(3,70px)", columnGap: 6, alignItems: "center" }}>
      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 28, height: 28, flex: "none", borderRadius: "50%", background: "#1C1C21", boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", font: "800 9px 'Manrope'", color: "#E4E4E7" }}>{badgeInitials(name)}</span>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "700 15px 'Outfit'", color: "#fff" }}>{name}</span>
      </div>
      <Cell o={c.spread} line={c.spread ? signed(c.spread.line) : null} locked={locked} oddsFmt={oddsFmt} choose={choose} pickedO={pickedO} />
      <Cell o={c.total} line={c.total ? `${side === "away" ? "O" : "U"} ${c.total.line}` : null} locked={locked} oddsFmt={oddsFmt} choose={choose} pickedO={pickedO} />
      <Cell o={c.ml} line={null} locked={locked} oddsFmt={oddsFmt} choose={choose} pickedO={pickedO} />
    </div>
  );
  return (
    <div style={{ margin: "0 16px", borderRadius: 18, overflow: "hidden", background: "linear-gradient(180deg,#17171B 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.09),0 12px 28px rgba(0,0,0,.35)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) repeat(3,70px)", columnGap: 6, alignItems: "center", height: 40, padding: "0 12px", background: "#0F0F12", borderBottom: "1px solid #1A1A1E" }}>
        <span style={{ font: "800 14px 'Manrope'", letterSpacing: ".06em" }}>Game lines</span>
        {["SPREAD", "TOTAL", "ML"].map((l) => <span key={l} style={{ textAlign: "center", font: "700 10px 'Outfit'", letterSpacing: ".12em", color: "#71717a" }}>{l}</span>)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "12px" }}>
        {row("away", ev.away_team, away)}
        {row("home", ev.home_team, home)}
      </div>
    </div>
  );
}

function Cell({ o, line, locked, oddsFmt, choose, pickedO }) {
  if (!o) return <span style={{ height: 46, borderRadius: 12, boxShadow: "inset 0 0 0 1px #1A1A1E", display: "flex", alignItems: "center", justifyContent: "center", font: "700 14px 'Manrope'", color: "#3F3F46" }}>—</span>;
  const isLock = locked || o.suspended === true || o.active === false;
  if (isLock) return (
    <span title="Market suspended" style={{ height: 46, borderRadius: 12, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
      {line && <span style={{ font: "600 11px 'Outfit'", color: "#52525b" }}>{line}</span>}
      <Icon name="lock" size={13} style={{ color: "#52525b" }} />
    </span>
  );
  const sel = pickedO(o);
  return (
    <button type="button" onClick={() => choose(o)} aria-pressed={!!sel}
      style={{ height: 46, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, border: 0, cursor: "pointer",
        background: sel ? "#00D632" : "#18181C", boxShadow: sel ? "inset 0 1px 0 rgba(255,255,255,.32)" : "inset 0 0 0 1px #26262B,inset 0 1px 0 rgba(255,255,255,.04)" }}>
      {line && <span style={{ font: "600 11px 'Outfit'", color: sel ? "rgba(10,10,10,.7)" : "#a1a1aa", whiteSpace: "nowrap" }}>{line}</span>}
      <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: sel ? "#0A0A0A" : "#fff", whiteSpace: "nowrap" }}>{formatOdds(o.odds, oddsFmt)}</span>
    </button>
  );
}

function PropRow({ player, row, last, oddsFmt, choose, pickedO }) {
  const { over, under, line, yesno } = row;
  const img = over?.player_image_url || under?.player_image_url || null;
  const side = (o, label) => {
    if (!o) return <span style={{ height: 48, borderRadius: 12, boxShadow: "inset 0 0 0 1px #1A1A1E", display: "flex", alignItems: "center", justifyContent: "center", font: "700 14px 'Manrope'", color: "#3F3F46" }}>—</span>;
    const isLock = o.suspended === true || o.active === false;
    if (isLock) return <span title="Market suspended" style={{ height: 48, borderRadius: 12, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="lock" size={13} style={{ color: "#52525b" }} /></span>;
    const sel = pickedO(o);
    return (
      <button type="button" onClick={() => choose(o)} aria-pressed={!!sel}
        style={{ height: 48, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, border: 0, cursor: "pointer",
          background: sel ? "#00D632" : "#18181C", boxShadow: sel ? "inset 0 1px 0 rgba(255,255,255,.32)" : "inset 0 0 0 1px #26262B,inset 0 1px 0 rgba(255,255,255,.04)" }}>
        <span style={{ font: "600 10px 'Outfit'", color: sel ? "rgba(10,10,10,.7)" : "#71717a" }}>{label}</span>
        <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: sel ? "#0A0A0A" : "#fff" }}>{formatOdds(o.odds, oddsFmt)}</span>
      </button>
    );
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 44px 74px 74px", columnGap: 6, alignItems: "center", padding: "10px 12px", borderBottom: last ? "none" : "1px solid #1A1A1E" }}>
      <div style={{ display: "flex", minWidth: 0, alignItems: "center", gap: 10 }}>
        <span style={{ width: 40, height: 40, flex: "none", borderRadius: "50%", overflow: "hidden", background: "radial-gradient(circle at 50% 30%, #26262B 0%, #18181C 70%)", boxShadow: "inset 0 0 0 1px #2F2F35", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {img ? <img src={img} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
            : <span style={{ font: "800 12px 'Manrope'", color: "#D4D4D8" }}>{playerInitials(player)}</span>}
        </span>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "700 14px 'Outfit'", color: "#fff" }}>{player}</span>
      </div>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        {!yesno && <>
          <span style={{ font: "700 9px 'Outfit'", letterSpacing: ".16em", color: "#52525b" }}>LINE</span>
          <span className="m-tnum" style={{ font: "800 15px 'Manrope'", color: "#fff" }}>{line ?? "—"}</span>
        </>}
      </span>
      {side(over, yesno ? "Yes" : "Over")}
      {side(under, yesno ? "No" : "Under")}
    </div>
  );
}

function TeamCol({ name }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 0 }}>
      <span style={{ width: 44, height: 44, flex: "none", borderRadius: "50%", background: "#1C1C21", boxShadow: "inset 0 0 0 2px rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", font: "800 12px 'Manrope'", color: "#E4E4E7" }}>{badgeInitials(name)}</span>
      <span style={{ font: "700 14px 'Outfit'", color: "#fff", whiteSpace: "nowrap", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>{shortTeam(name)}</span>
    </div>
  );
}

function shortTeam(name = "") {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  return w[w.length - 1] || name;
}
function prettyProp(ps = "") {
  if (!ps) return "";
  // Turn "PassingYds" / "ReceivingRecs" into "Passing Yds" etc.
  return String(ps).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}

function Empty({ text }) {
  return <p style={{ textAlign: "center", color: "#71717a", font: "500 13px 'Outfit'", padding: "28px 16px" }}>{text}</p>;
}
function EventSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12 }}>
      <div style={{ height: 60, margin: "0 16px", borderRadius: 16, background: "#141417" }} />
      <div style={{ height: 40, margin: "0 16px", borderRadius: 12, background: "#141417" }} />
      <div style={{ height: 160, margin: "0 16px", borderRadius: 18, background: "#141417" }} />
    </div>
  );
}
